import format from 'date-fns/format'
import React, { FC } from 'react'
import { Box, Flex, Text, ButtonOutline } from 'front-end/src/components'

import { Trans } from '@lingui/macro'

import {
  CertificationItemTypes,
  PathwayEnrolmentStatusEnum,
  WorkbookStatusEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import IconX from 'front-end/src/components/common/icon/IconX'
import { formatDateAndTimeLong } from 'front-end/src/utils/dateHelpers'

import IconClock from '../../components/common/icon/IconClock'
import IconDoc from '../../components/common/icon/IconDoc'
import IconTick from '../../components/common/icon/IconTick'
import { getItemModalTypes } from '../certifications-page/manage/ItemModal'
import { linkBuilder } from '../../../../shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'
import { TableCol, TableRow } from '../../components/common/table/Table'
import { TypeOfElement } from '../../../../types/generic'
import { CertificationEnrolmentQuery } from './CertificationPage.codegen'
import { useRetryResourceMutation } from '../topic-detail-page/RetryResourceQuiz.codegen'
import { brandColors } from 'shared/basicPalette'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import { NbSp } from 'front-end/src/utils/NbSp'

export const MainColumn: FC<IProps> = ({ item }) => {
  const title =
    item.topic?.currentTitle || item.pathway?.title || item.event?.title
  const itemModalTypes = getItemModalTypes()

  return (
    <TableCol>
      <Flex align="center">
        <Text
          fontSize={['md', 'lg']}
          color={brandColors.grey[400]}
          mr={['16px', '24px']}
        >
          {itemModalTypes[item.item_type].icon}
        </Text>
        <Box>
          <Text
            mb="1"
            fontSize={fontSizes.sm}
            marginBottom="0"
            lineHeight="15px"
            color={brandColors.grey[400]}
          >
            {itemModalTypes[item.item_type].label}
          </Text>
          <Text fontSize={fontSizes.lg} lineHeight="22px" mt="6px">
            {title}
          </Text>
        </Box>
      </Flex>
    </TableCol>
  )
}

export const MiscColumn: FC<IProps> = ({ item }) => {
  return (
    <TableCol>
      <Flex fontSize={fontSizes.sm} color={brandColors.grey[400]}>
        {item.item_type === CertificationItemTypes.pathway && (
          <>
            <IconDoc boxSize="22px" pr="8px" />
            {item.pathway?.numberOfTopics}
            <NbSp />
            <Trans>resources</Trans>
          </>
        )}
        {item.item_type === CertificationItemTypes.event &&
          item.event?.start_time &&
          item.event?.end_time && (
            <Box fontSize={['11px', '13px']}>
              <Text display={['none', 'inline']}>
                <IconClock css={{ paddingRight: '8px' }} />
              </Text>
              {formatDateAndTimeLong(item.event?.start_time)}
              {format(new Date(item.event?.end_time), ' — h:mm a')}
            </Box>
          )}
      </Flex>
    </TableCol>
  )
}

interface IProps {
  item: TypeOfElement<
    NonNullable<
      CertificationEnrolmentQuery['me']['certificationEnrolment']
    >['certification']['items']
  >
}

interface ItemProps extends IProps {
  certId: number
}

export const CertificationItem: FC<ItemProps> = ({ certId, item }) => {
  let completed = false
  let failed = false
  let allowRetry = false
  let href = ''
  const [retryResource] = useRetryResourceMutation({
    variables: {
      topicId: item.item_id
    }
  })

  const retry = async () => {
    await retryResource()
    navigateTo(href)
  }

  switch (item.item_type) {
    case CertificationItemTypes.event:
      completed = !!item.event?.currentUserInvitation?.present
      href = linkBuilder.eventDetail(item.item_id)
      break

    case CertificationItemTypes.topic:
      completed = !!item.topic?.lastEnrolment?.isCompleted
      failed = item.topic?.lastEnrolment?.status === WorkbookStatusEnum.FAILED
      allowRetry = !!(
        item.topic?.lastEnrolment?.topicVersion?.allow_retry &&
        !item.topic?.lastEnrolment?.isMaxAttemptsExceeded
      )
      href = linkBuilder.topicUrlViaCertification(certId, item.item_id)
      break

    case CertificationItemTypes.pathway:
      completed = !!item.pathway?.currentEnrolment?.isCompleted
      failed =
        item.pathway?.currentEnrolment?.status ===
        PathwayEnrolmentStatusEnum.FAILED
      href = linkBuilder.pathwayDetail(item.item_id)
      break

    default:
      break
  }

  return (
    <TableRow condensedForMobile withHover onClick={() => navigateTo(href)}>
      <MainColumn item={item} />
      <MiscColumn item={item} />
      <TableCol>
        <Flex
          justifyContent={'flex-end'}
          color={brandColors.grey.light}
          textAlign="right"
        >
          {completed ? (
            <IconTick size="normal" />
          ) : failed ? (
            <Flex>
              {allowRetry && (
                <ButtonOutline onClick={retry} size="sm">
                  <Trans>Retry</Trans>
                </ButtonOutline>
              )}

              <Flex ml="8px" alignSelf="center">
                <IconX style={{ width: '24px', height: '24px' }} />
              </Flex>
            </Flex>
          ) : (
            <Box
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '1px solid rgb(177,191,197)'
              }}
            />
          )}
        </Flex>
      </TableCol>
    </TableRow>
  )
}
